import { Component, Input, OnChanges, SimpleChanges } from "@angular/core";
import { LoadingService } from "src/services/loading/loading.service";
import { UserApiService } from "src/services/user/user-api.service";
import { OrderHistoryService } from "src/services/history/order-history.service";
import { BaseHistoryListComponent, CompleteHistoryLog, CompleteOrderHistoryLog } from "../base-history-list.component";
import { DriverApiService } from "src/services/driver/driver-api.service";
import { removeDuplicates, removeFalsyValues } from "src/services/utils/array.utils";
import { driverToString } from "src/services/driver/driver.interface";

import { assertNever } from "src/services/utils/enum.utils";

type OrderHistoryWithDriver = Omit<CompleteHistoryLog, "driverId"> & { driverId: string };

@Component({
  selector: "rc-order-history-list",
  templateUrl: "../base-history-list.component.html",
  styleUrls: ["../base-history-list.component.scss"],
  standalone: false,
})
export class OrderHistoryListComponent extends BaseHistoryListComponent implements OnChanges {
  @Input() public orderId: string;

  constructor(
    protected readonly userService: UserApiService,
    private readonly driverService: DriverApiService,
    private readonly loadingService: LoadingService,
    private readonly orderHistoryService: OrderHistoryService,
  ) {
    super(userService);
  }

  public async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (changes.orderId && this.orderId) {
      await this.initData();
    }
  }

  protected async initData(): Promise<void> {
    if (!this.orderId) {
      return;
    }
    this.loading = true;
    await this.loadingService.wrapInLoading(async () => {
      this.historyLogs = await this.orderHistoryService.getHistoryByOrderId(this.orderId);
      await this.populateUsers(this.historyLogs.map((log) => log.user.userId));
      this.populateIcons();
      this.loading = false;
      this.loaded = true;
      await this.populateDrivers();
    });
  }

  private async populateDrivers(): Promise<void> {
    const logsWithDriver = this.historyLogs.filter((log) => "driverId" in log) as OrderHistoryWithDriver[];

    const driverIds = removeFalsyValues(
      logsWithDriver.map((log) => {
        return log.driverId;
      }),
    );

    if (driverIds.length === 0) {
      return;
    }

    const allDrivers = await this.driverService.getByIds(removeDuplicates(driverIds));

    const allDriversMaps = {};
    for (const driver of allDrivers) {
      allDriversMaps[driver.id] = driver;
    }

    for (const log of logsWithDriver) {
      log.fullDriver = allDriversMaps[log.driverId];
    }
  }

  private populateIcons(): void {
    for (const log of this.historyLogs as CompleteOrderHistoryLog[]) {
      switch (log.action) {
        case "addedToCalendar":
          log.icon = "calendar-outline";
          break;
        case "assignDriver":
          log.icon = "car-outline";
          break;
        case "acceptOrder":
          log.icon = "car-outline";
          break;
        case "createOrder":
          log.icon = "add-circle-outline";
          break;
        case "deliveryCompleted":
        case "deliveryStarted":
          log.icon = "arrow-down-outline";
          break;
        case "dropAtWarehouse":
          log.icon = "home-outline";
          break;
        case "onboardingCompleted":
          log.icon = "checkmark-circle-outline";
          break;
        case "pickupCompleted":
        case "pickupStarted":
          log.icon = "arrow-up-outline";
          break;
        case "rejectOrder":
          log.icon = "car-outline";
          break;
        case "stagingCompleted":
        case "storingCompleted":
          log.icon = "checkmark-circle-outline";
          break;
        case "updateAddress":
          log.icon = "map-outline";
          break;
        case "updateFulfillmentDatetime":
          log.icon = "calendar-outline";
          break;
        case "uploadPicture":
          log.icon = "camera-outline";
          break;
        case "updateStatus":
          log.icon = "attach-outline";
          break;
        default:
          assertNever(log.action);
      }
    }
  }

  public readonly driverToString = driverToString;
}
